import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { differenceInCalendarDays, format, isValid, parseISO, startOfToday, subDays } from 'date-fns';

/**
 * URL-backed performance date range. Picks one of a fixed set of windows via
 * `?range=`, or a free-form window via `?range=custom&from=…&to=…`, and resolves
 * to ISO `from` / `to` for the insights API plus a `lookback` for the
 * previous-period comparison. Hooks that fetch insights take the resolved values
 * directly so the SWR cache key naturally invalidates when the window changes.
 *
 * Nothing server-side caps the window — `/api/insights` batches rows rather than
 * truncating, and the Local Ads handler derives its comparison window from
 * whatever `from`/`to` it is given — so the option list here is the only thing
 * that decides how far back a retailer can look (NEA-6380).
 *
 * The label is what the design shows on the "Showing data for" pill — kept
 * here so the page and the picker don't drift apart.
 */
export const DATE_RANGE_OPTIONS = [
	{ value: '7d', label: 'Last 7 days', days: 7 },
	{ value: '30d', label: 'Last 30 days', days: 30 },
	{ value: '90d', label: 'Last 90 days', days: 90 },
	{ value: '6m', label: 'Last 6 months', days: 183 },
	{ value: '12m', label: 'Last 12 months', days: 365 }
];

export const CUSTOM_RANGE = 'custom';

/** Nothing in the insights tables predates this, and it matches the NearSt picker's floor. */
export const EARLIEST_DATE = '2020-01-01';

const DEFAULT_RANGE = '30d';

const LONGEST_OPTION = DATE_RANGE_OPTIONS.reduce((longest, option) => (option.days > longest.days ? option : longest));

/** A custom window can't be longer than the longest preset — see `resolveCustomRange`. */
export const MAX_RANGE_DAYS = LONGEST_OPTION.days;

/** "12 months" — how the picker phrases that ceiling. Derived, so a new longest preset carries. */
export const MAX_RANGE_LABEL = LONGEST_OPTION.label.replace(/^Last /, '');

/**
 * Local-calendar ISO date. `toISOString()` would convert to UTC first, which
 * shifts the day either side of midnight for anyone not on UTC — enough to make
 * a custom range fail to round-trip through the URL.
 */
export const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const today = () => startOfToday();

const parseIsoDate = (value) => {
	if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
	const parsed = parseISO(value);
	return isValid(parsed) ? parsed : null;
};

/** "1 Jan – 14 Mar 2026", or "1 Nov 2025 – 14 Mar 2026" when the range straddles a year. */
const customLabel = (from, to) =>
	from.getFullYear() === to.getFullYear()
		? `${format(from, 'd MMM')} – ${format(to, 'd MMM yyyy')}`
		: `${format(from, 'd MMM yyyy')} – ${format(to, 'd MMM yyyy')}`;

/**
 * The earliest `from` selectable against a given `to`: the longest preset back,
 * floored at the earliest data we hold. The picker uses it for the From input's
 * `min` so the UI can't offer a date the hook would silently snap.
 */
export const earliestFromFor = (toValue) => {
	const to = parseIsoDate(toValue) ?? today();
	const earliest = parseIsoDate(EARLIEST_DATE);
	const spanFloor = subDays(to, MAX_RANGE_DAYS);
	return isoDate(spanFloor > earliest ? spanFloor : earliest);
};

/**
 * Resolve `?from=`/`?to=` into a window, clamping to the earliest data we hold,
 * to today, and to `MAX_RANGE_DAYS`. Returns null for anything unusable so the
 * caller can fall back to the default preset rather than render an empty page.
 */
const resolveCustomRange = (fromParam, toParam) => {
	const from = parseIsoDate(fromParam);
	const to = parseIsoDate(toParam);
	if (!from || !to) return null;

	const earliest = parseIsoDate(EARLIEST_DATE);
	const latest = today();
	const clampedTo = to > latest ? latest : to;
	// Cap the SPAN, not just the two endpoints. Nothing server-side bounds the
	// window — `insights.js` pages rather than truncating, and `resolveDateWindows`
	// only checks `from <= to` — so an uncapped custom range fans a multi-year read,
	// plus a same-length lookback beneath it, across every query on the page. Past
	// ~500 days the `gmb` read also stops fitting one `INSIGHTS_ROW_BATCH_LIMIT`
	// page, and the handler re-runs its GROUP BY once per page thereafter.
	const spanFloor = subDays(clampedTo, MAX_RANGE_DAYS);
	const lowerBound = spanFloor > earliest ? spanFloor : earliest;
	const clampedFrom = from < lowerBound ? lowerBound : from;
	if (clampedFrom > clampedTo) return null;

	return {
		from: clampedFrom,
		to: clampedTo,
		days: differenceInCalendarDays(clampedTo, clampedFrom),
		label: customLabel(clampedFrom, clampedTo),
		value: CUSTOM_RANGE
	};
};

const resolvePresetRange = (value) => {
	const option = DATE_RANGE_OPTIONS.find((o) => o.value === value) ?? DATE_RANGE_OPTIONS.find((o) => o.value === DEFAULT_RANGE);
	const to = today();
	return { from: subDays(to, option.days), to, days: option.days, label: option.label, value: option.value };
};

const buildRange = ({ from, to, days, label, value }) => {
	const lookbackTo = subDays(from, 1);
	const lookbackFrom = subDays(lookbackTo, days);
	return {
		from: isoDate(from),
		to: isoDate(to),
		lookbackFrom: isoDate(lookbackFrom),
		lookbackTo: isoDate(lookbackTo),
		days,
		label,
		value,
		// Drops into sentences like "How this channel is performing over ___."
		// A preset reads "the last 30 days"; a custom window reads as its dates.
		periodCopy: value === CUSTOM_RANGE ? label : `the ${label.toLowerCase()}`,
		isCustom: value === CUSTOM_RANGE,
		isDefault: value === DEFAULT_RANGE
	};
};

export function useDateRange() {
	const [searchParams, setSearchParams] = useSearchParams();
	const value = searchParams.get('range') ?? DEFAULT_RANGE;
	const fromParam = searchParams.get('from');
	const toParam = searchParams.get('to');

	const range = useMemo(() => {
		const custom = value === CUSTOM_RANGE ? resolveCustomRange(fromParam, toParam) : null;
		return buildRange(custom ?? resolvePresetRange(value));
	}, [value, fromParam, toParam]);

	const setRange = (next) => {
		const params = new URLSearchParams(searchParams);
		params.delete('from');
		params.delete('to');
		if (next === DEFAULT_RANGE) params.delete('range');
		else params.set('range', next);
		setSearchParams(params, { replace: true });
	};

	const setCustomRange = (from, to) => {
		const params = new URLSearchParams(searchParams);
		params.set('range', CUSTOM_RANGE);
		params.set('from', from);
		params.set('to', to);
		setSearchParams(params, { replace: true });
	};

	return { ...range, setRange, setCustomRange };
}
